import uniqid from 'uniqid';
import { useHistoryTravel } from 'ahooks';
import React, { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Group, Shape, Rect, Line, Stage, Layer } from 'react-konva';
import { getTempState, setSharedData, setTempState } from '@starflow/app/store';
import { Loader, Tooltip, Popover, Radio, Group as RadioGroup, Input } from '@mantine/core';
import { useDebounceDisclosure } from './hooks/useDebounceDisclosure';
import { Icon } from '@shakkerai/components';
import { translate } from '@shakkerai/util-i18n';
import { useViewport } from '@starflow/app/store';
import { calcShapeArea, fetchImageSegmentShapes, getImageSegmentCacheKey } from '@shakkerai/pro-utils';
import { getImageOssPath } from '@shakkerai/util-image';
import imgInpaintMask from '@shakkerai/pro-canvas/src/assets/inpaint_mask.png';
import imgInpaintMaskInvert from '@shakkerai/pro-canvas/src/assets/inpaint_mask_invert.png';
import { PopoverSlider } from './PopoverSlider';
import { submitTranslate } from '@shakkerai/services/src/canva';
import { inferenceAPIBucket } from '@shakkerai/pro-utils';
import { requestSegmentImage } from '@shakkerai/services';
import { notifySuccess, notifyError } from '@shakkerai/pro-components';
import autoSelection from './svg/auto-selection.svg';
import downPull from './svg/down-pull.svg';
// 终极抽离版本

const SELECTION_COLOR = '#8053ff';
const selectionStrokeDash = [8, 6];

export const SegmentShape = ({
  index,
  fill,
  opacity,
  hoverOpacity,
  selectable,
  onClick,
  segment,
  tool,
  canvasCssWidth,
  canvasCssHeight,
}) => {
  const [hover, setHover] = useState(false);

  if (typeof opacity === 'undefined') {
    opacity = 0;
  }

  if (typeof hoverOpacity === 'undefined') {
    hoverOpacity = 0.85;
  }
  // 只有这三中类型有shape的hover事件
  if (!(tool === 'segment' || tool === 'autoSelectArea' || tool === 'mainSelect')) return null;
  return (
    <Shape
      index={index}
      listening={selectable}
      onClick={() => onClick(segment.points, segment.area, segment.id)}
      fill={fill || SELECTION_COLOR}
      opacity={hover ? hoverOpacity : opacity}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      sceneFunc={(ctx, shape) => {
        ctx.beginPath();
        ctx.moveTo(segment.points[0][0] * canvasCssWidth, segment.points[0][1] * canvasCssHeight);

        segment.points.forEach((point) => {
          ctx.lineTo(point[0] * canvasCssWidth, point[1] * canvasCssHeight);
        });

        ctx.closePath();
        ctx.fillStrokeShape(shape);
      }}
    />
  );
};

export const LassoShape = ({
  selection,
  fill,
  opacity,
  hoverOpacity,
  selected,
  showStroke,
  elementScale,
  hoverable = true,
  scale,
}) => {
  const [hover, setHover] = useState(false);

  if (typeof opacity === 'undefined') {
    opacity = 0.4;
  }

  if (typeof hoverOpacity === 'undefined') {
    hoverOpacity = 0.6;
  }

  // extend = extend || 0;
  showStroke = showStroke || selected;

  const sceneFunc = (ctx, shape) => {
    ctx.beginPath();
    ctx.moveTo(selection.points[0][0], selection.points[0][1]);

    selection.points.forEach((point) => {
      ctx.lineTo(point[0], point[1]);
    });

    ctx.closePath();
    ctx.fillStrokeShape(shape);
  };
  return (
    <Group>
      {/* <Shape
        id={selection.id}
        name={'selection-' + selection.id}
        globalCompositeOperation={extend ? 'destination-out' : undefined}
        stroke={fill || SELECTION_COLOR}
        strokeWidth={(extend * 2) / elementScale}
        fill={fill || SELECTION_COLOR}
        opacity={!extend ? 0 : selected || hover ? hoverOpacity : opacity}
        onMouseEnter={() => hoverable && setHover(true)}
        onMouseLeave={() => hoverable && setHover(false)}
        sceneFunc={sceneFunc}
      /> */}

      <Shape
        id={selection.id}
        name={'selection-' + selection.id}
        fill={fill || SELECTION_COLOR}
        opacity={selected || hover ? hoverOpacity : opacity}
        globalCompositeOperation="source-over"
        sceneFunc={sceneFunc}
        onMouseEnter={() => hoverable && setHover(true)}
        onMouseLeave={() => hoverable && setHover(false)}
      />
      {showStroke && (
        <Shape
          listening={false}
          stroke="#fff"
          dash={selectionStrokeDash}
          strokeWidth={2}
          opacity={selected || hover ? hoverOpacity : opacity}
          sceneFunc={sceneFunc}
        />
      )}
    </Group>
  );
};

export const LineShape = (props) => {
  if (!props.points) return null;
  const f = props.points.flat();
  const strokeWidth = props.size || 1;
  return (
    <Line
      points={f}
      stroke={SELECTION_COLOR}
      strokeWidth={strokeWidth}
      // strokeWidth={1}
      tension={0.5}
      lineCap="round"
      lineJoin="round"
      opacity={props.type === 'eraser' ? 1 : 0.5}
      globalCompositeOperation={props.type === 'eraser' ? 'destination-out' : 'source-over'}
    />
  );
};

export const RectShape = ({
  selection,
  fill,
  extend,
  opacity,
  hoverOpacity,
  selected,
  showStroke,
  elementScale,
  hoverable = true,
  scale,
}) => {
  const [hover, setHover] = useState(false);

  if (selection.points.length < 2) return null;

  const x = Math.min(selection.points[1][0], selection.points[0][0]);
  const y = Math.min(selection.points[0][1], selection.points[1][1]);
  const width = Math.abs(selection.points[1][0] - selection.points[0][0]);
  const height = Math.abs(selection.points[0][1] - selection.points[1][1]);

  if (typeof opacity === 'undefined') {
    opacity = 0.4;
  }

  if (typeof hoverOpacity === 'undefined') {
    hoverOpacity = 0.6;
  }

  return (
    <>
      <Rect
        id={selection.id}
        name={'selection-' + selection.id}
        stroke={fill || SELECTION_COLOR}
        strokeWidth={extend}
        x={x - extend / 2}
        y={y - extend / 2}
        width={width + extend}
        height={height + extend}
        opacity={selected || hover ? hoverOpacity : opacity}
        onMouseEnter={() => hoverable && setHover(true)}
        onMouseLeave={() => hoverable && setHover(false)}
      />
      <Rect
        x={x}
        y={y}
        listening={false}
        width={width}
        height={height}
        stroke="#fff"
        dash={selectionStrokeDash}
        strokeWidth={showStroke || selected ? 1 / scale / elementScale : 0}
        fill={fill || SELECTION_COLOR}
        opacity={selected || hover ? hoverOpacity : opacity}
      />
    </>
  );
};

export const defaultValue = {
  selected: null,
  selections: [],
  shapes: [],
  tool: 'brush',
  extend: 0,
  brushSize: 100,
  eraserSize: 100,
  segmentType: 'normal',
  autoSegmenting: false,
  mode: null,
  text: '',
};

export const defaultConfig = {
  showToolbar: true,
};

export const useSegmentEditorState = (initialValue = defaultValue) => {
  const refValue = useRef(initialValue);
  const editorState = useHistoryTravel(initialValue);

  const setState = useCallback((partialState) => {
    const nextState = Object.assign({}, refValue.current, partialState);
    editorState.setValue((refValue.current = nextState));
  }, []);

  const getState = useCallback(() => {
    return refValue.current;
  }, []);

  useEffect(() => {
    refValue.current = editorState.value;
  }, [editorState.value]);

  return [editorState, setState, getState];
};

export const SelectionItem = React.memo(
  ({ key, data, extend = 0, currentType, selected, elementScale, scale, canvasCssWidth, canvasCssHeight, onClick }) => {
    const isDrawingTool = currentType === 'brush' || currentType === 'eraser';
    const newData = {
      ...data,
      points: data.points.map((item) => {
        return [item[0] * canvasCssWidth, item[1] * canvasCssHeight];
      }),
    };

    if (data.type === 'lasso') {
      return (
        <LassoShape
          hoverable={!isDrawingTool}
          selection={newData}
          extend={extend}
          selected={selected}
          elementScale={elementScale}
          scale={scale}
          onClick={() => {
            if (selected) {
              onclick(data.points, data.area, key);
            }
          }}
        />
      );
    }

    if (data.type === 'rect') {
      return (
        <RectShape
          hoverable={!isDrawingTool}
          selection={newData}
          extend={extend}
          selected={selected}
          elementScale={elementScale}
          scale={scale}
          onClick={() => {
            if (selected) {
              onclick(data.points, data.area, key);
            }
          }}
        />
      );
    }

    if (data.type === 'brush' || data.type === 'eraser') {
      return (
        <LineShape
          points={newData.points}
          size={data.size * scale}
          type={data.type}
          elementScale={elementScale}
          scale={scale}
          onClick={() => {
            if (selected) {
              onclick(data.points, data.area, key);
            }
          }}
        />
      );
    }

    return null;
  },
);

export const SegmentEditor = React.memo((props) => {
  const {
    className = '',
    stageClassName,
    element,
    editorState,
    setEditorState,
    getEditorState,
    config = defaultConfig,
    isShapeCache,
    inputCanvasScale,
  } = props;
  const { autoSegment, showToolbar } = config;
  const { selections, selected, extend, brushSize, eraserSize, tool, segmentType, shapes, autoSegmenting } =
    editorState.value;

  const tempRef = useRef({});
  const elementScale = Math.max(element.scaleX, element.scaleY);
  const canvasScale = inputCanvasScale || useViewport().scale * elementScale;
  const [drawingSize, setDrawingSize] = useState(0);
  const [drawingPoints, setDrawingPoints] = useState(null);

  const canvasCssWidth = element.width * canvasScale;
  const canvasCssHeight = element.height * canvasScale;

  const appendSelection = useCallback((selection) => {
    let { type, area, sourceType, points, size } = selection;

    sourceType = sourceType || type;
    sourceType = sourceType || type;
    area = area || calcShapeArea(selection);

    const id = uniqid();
    const selectionData = { id, type, sourceType, points };

    if (size) {
      selectionData.size = size;
      selectionData.area = 0;
    }

    if (type !== 'brush' && type !== 'eraser') {
      selectionData.area = area || calcShapeArea(selection);
    }

    const nextSelections = getEditorState().selections.concat([selectionData]);

    setEditorState({
      selected: id,
      selections: nextSelections || [],
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const removeSelection = useCallback((id) => {
    setEditorState({
      selections: getEditorState().selections.filter((item) => {
        return item.id !== id;
      }),
    });
  }, []);
  const handleMousEnter = useCallback(() => {
    if (tool !== 'segment') {
      document.body.style.cursor = 'crosshair';
    }
  }, [tool]);

  const handleMouseLeave = useCallback(() => {
    document.body.style.cursor = 'auto';
  }, []);

  const handleMouseDown = useCallback(
    (event) => {
      if (event.target.name()?.indexOf('selection') === 0) {
        if (tool !== 'brush' && tool !== 'eraser') {
          const id = event.target.id();
          setEditorState({ selected: selected === id ? null : id });
          if (id) {
            removeSelection(id);
          }
          return;
        }
      }

      const point = event.evt;

      if (tool === 'brush') {
        setDrawingSize(brushSize);
      } else if (tool === 'eraser') {
        setDrawingSize(eraserSize);
      } else {
        setDrawingSize(0);
      }

      tempRef.current.mousedown = true;
      tempRef.current.type = tool;
      tempRef.current.elementScaleX = element.scaleX;
      tempRef.current.elementScaleY = element.scaleY;

      const mouseX = point.offsetX / canvasCssWidth;
      const mouseY = point.offsetY / canvasCssHeight;

      // setEditorState({ selected: null });
      setDrawingPoints([[mouseX, mouseY]]);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [canvasCssWidth, canvasCssHeight, brushSize, eraserSize, selected, tool],
  );

  const handleMouseMove = useCallback(
    (event) => {
      if (!tempRef.current.mousedown) return;

      const { type } = tempRef.current;

      if (type === 'segment') return;

      if (type === 'brush' || type === 'eraser' || type === 'lasso') {
        const point = event.evt;
        const mouseX = point.offsetX / canvasCssWidth;
        const mouseY = point.offsetY / canvasCssHeight;

        setDrawingPoints((points) => {
          return (tempRef.current.drawingPoints = points.concat([[mouseX, mouseY]]));
        });
      } else if (type === 'rect') {
        const point = event.evt;
        const mouseX = point.offsetX / canvasCssWidth;
        const mouseY = point.offsetY / canvasCssHeight;

        setDrawingPoints((points) => {
          return (tempRef.current.drawingPoints = [points[0], [mouseX, mouseY]]);
        });
      }
    },
    [canvasCssWidth, canvasCssHeight],
  );

  const handleMouseUp = useCallback(() => {
    tempRef.current.mousedown = false;

    const { type, drawingPoints } = tempRef.current;
    const selectionData = { type, points: drawingPoints };

    if (!drawingPoints) return;
    if (type === 'segment') return;

    if (type === 'brush' || type === 'eraser') {
      if (drawingPoints.length < 1) return;
    } else if (type === 'lasso') {
      if (drawingPoints.length < 3) return;
    } else if (type === 'rect') {
      if (drawingPoints.length < 2) return;
    }

    if (drawingSize) {
      selectionData.size = drawingSize;
    }

    appendSelection(selectionData);
    setDrawingPoints(null);
    tempRef.current.drawingPoints = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drawingSize]);

  const handleKeyDown = useCallback((event) => {
    if (event.target.matches('input, textarea')) {
      return;
    }

    if (event.key === 'Delete' || event.key === 'Backspace') {
      const { selected, selections } = getEditorState();

      if (selected !== null) {
        setEditorState({
          selected: null,
          selections: selections.filter((item) => item.id !== selected),
        });
        event.stopPropagation();
        event.preventDefault();
      }
    } else if (event.key === 'z' && (event.metaKey || event.ctrlKey)) {
      if (event.shiftKey) {
        editorState.forward();
      } else {
        editorState.back();
      }
    } else if (event.key === 'a' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault();
    }
  }, []);

  const handleClickSegment = useCallback((points, area, id) => {
    // 判断id是否存在，不存在就appendSeclection ,存在就剔除（我理解应该是这样，但是线上目前还不是这样）
    const selction = getEditorState().selections.find((item) => {
      return item.id === id;
    });
    if (selction) {
      removeSelection(id);
    } else {
      appendSelection({ type: 'lasso', area, sourceType: 'segment', points });
    }
    // appendSelection({ type: 'lasso', area, sourceType: 'segment', points });
  }, []);

  const drawingSelection = useMemo(() => {
    if (!drawingPoints) return null;

    const currentDrawingPoint = drawingPoints.map((item) => {
      return [item[0] * canvasCssWidth, item[1] * canvasCssHeight];
    });

    if (tool === 'lasso') {
      return (
        <LassoShape
          showStroke
          hoverable={false}
          selection={{ id: 'drawing', type: 'lasso', points: currentDrawingPoint }}
          elementScale={elementScale}
          scale={canvasScale}
        />
      );
    }

    if (tool === 'brush' || tool === 'eraser') {
      return (
        <LineShape
          type={tool}
          size={drawingSize * canvasScale}
          points={currentDrawingPoint}
          elementScale={elementScale}
          scale={canvasScale}
        />
      );
    }

    if (tool === 'rect') {
      return (
        <RectShape
          showStroke
          hoverable={false}
          selection={{ id: 'drawing', type: 'rect', points: currentDrawingPoint }}
          elementScale={elementScale}
          scale={canvasScale}
        />
      );
    }
  }, [elementScale, canvasScale, tool, drawingPoints, drawingSize, canvasCssHeight, canvasCssWidth]);

  useEffect(() => {
    if (autoSegment) {
      fetchImageSegmentShapes(element.id);
    }
  }, [autoSegment, element.id]);

  useEffect(() => {
    setSharedData('preventUndoRedo', true);
    // 取出缓存的shape数据
    if (isShapeCache) {
      setEditorState({
        selections: getTempState('shapesCache'),
      });
    }
    return () => {
      setSharedData('preventUndoRedo', false);
    };
  }, []);

  useEffect(() => {
    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.cursor = 'auto';
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  return (
    <div
      // todos fqy 这里transform先去掉，看看哪里会有尺寸不对的东西
      // style={{
      //   transform: `scale(${element.scaleX}, ${element.scaleY})`,
      // }}
      className={`${className} relative origin-top-left pointer-events-auto`}
    >
      {autoSegmenting && (
        <div className="absolute inset-0 z-10 flex items-center justify-center overflow-hidden bg-black/60">
          <span className="text-xs text-white whitespace-nowrap">{translate('selection_loading')}</span>
        </div>
      )}
      <Stage
        className={`${stageClassName}`}
        width={canvasCssWidth}
        height={canvasCssHeight}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseEnter={handleMousEnter}
        onMouseLeave={handleMouseLeave}
      >
        <Layer>
          <Group>
            {Array.isArray(shapes) &&
              shapes.map((segment, index) => (
                <SegmentShape
                  selectable
                  key={segment?.id}
                  index={index}
                  segment={segment}
                  tool={tool}
                  onClick={handleClickSegment}
                  canvasCssWidth={canvasCssWidth}
                  canvasCssHeight={canvasCssHeight}
                />
              ))}
            {selections?.map((item) => (
              <SelectionItem
                key={item?.id}
                data={item}
                extend={extend}
                scale={canvasScale}
                currentType={tool}
                elementScale={elementScale}
                selected={selected === item.id}
                canvasCssWidth={canvasCssWidth}
                canvasCssHeight={canvasCssHeight}
                onClick={handleClickSegment}
              />
            ))}
            {drawingSelection}
          </Group>
        </Layer>
      </Stage>
      {showToolbar && (
        <SegmentEditorTools config={config} editorState={editorState} setState={setEditorState} element={element} />
      )}
    </div>
  );
});

SegmentEditor.defaultValue = defaultValue;
SegmentEditor.defaultConfig = defaultConfig;

export const SegmentsViewer = React.memo((props) => {
  const { shapes, element, width, height } = props;
  // console.log('xxx SegmentsViewer props', props)
  // 计算画布宽度
  let elementScale;
  let canvasCssWidth, canvasCssHeight;
  if (element.width > element.height) {
    canvasCssWidth = width;
    canvasCssHeight = (canvasCssWidth / element.width) * element.height;
    elementScale = element.width / canvasCssWidth;
  } else {
    canvasCssHeight = height;
    canvasCssWidth = (canvasCssHeight / element.height) * element.width;
    elementScale = element.height / canvasCssHeight;
  }
  return (
    <div className="w-full h-full flex justify-center items-center">
      <Stage width={canvasCssWidth} height={canvasCssHeight}>
        <Layer>
          <Group>
            {shapes.map((item) => (
              <SelectionItem
                data={item}
                key={item?.id}
                scale={1 / elementScale}
                elementScale={elementScale}
                canvasCssWidth={canvasCssWidth}
                canvasCssHeight={canvasCssHeight}
              />
            ))}
          </Group>
        </Layer>
      </Stage>
    </div>
  );
});

const selectedAreaTypeList = [
  {
    name: 'subject',
    label: translate('selection_text1'),
  },
  {
    name: 'face',
    label: translate('selection_text2'),
  },
  {
    name: 'skin',
    label: translate('selection_text3'),
  },
  {
    name: 'hair',
    label: translate('selection_text4'),
  },
  {
    name: 'custom',
    label: translate('selection_text5'),
  },
];

export const SegmentEditorTools = React.memo((props) => {
  const { segmenting, editorState, setState, config, element } = props;

  const currentImageTool = getTempState('topImagesTool');
  const isChangeFaceMode = currentImageTool === 'changeFace';
  const { shapes, tool, invert, extend = 0, brushSize = 100, eraserSize = 100 } = editorState.value;
  const { toolbarLeftButton, toolbarRightButton, noSegment, noFeathering, noClear, noInvert, noAutoSelection } = config;

  const canUndo = editorState.backLength > 0;
  const canRedo = editorState.forwardLength > 0;

  const [autoSelectingMode1, setAutoSelectingMode1] = useState(false);
  const [autoSelectingMode8, setAutoSelectingMode8] = useState(false);
  const lastSelectedAreaTypeLabel = useRef(null);
  // 自定义选区类型 先写在这里，后续考虑抽走
  const [selectedAreaType, setSelectedAreaType] = useState(selectedAreaTypeList[0].name);
  const [customText, setCustomText] = useState(selectedAreaTypeList[0].label);
  const selectedAreaTypeLabel = useMemo(() => {
    let res;
    if (selectedAreaType === 'custom') {
      res = customText;
    } else {
      const selectItem = selectedAreaTypeList.find((item) => item.name === selectedAreaType);
      res = selectItem.label || '';
    }
    return res;
  }, [selectedAreaType, customText]);
  const [opened, { close, open }] = useDebounceDisclosure();
  const handleSwitchType = useCallback(async (event) => {
    let currentType;
    if (typeof event === 'string') {
      currentType = event;
    } else {
      currentType = event.currentTarget.dataset.type;
    }

    setState({ tool: currentType });
  }, []);

  const handleAutoSelect = useCallback(
    async (mode) => {
      /*
        自动识别区域 如果是不是自定义类型直接请求，如果是自定义，先翻译再请求
      */
      let currentTypeRes;
      if (autoSelectingMode1 || autoSelectingMode8) return;
      if (
        mode === 8 &&
        lastSelectedAreaTypeLabel.current &&
        lastSelectedAreaTypeLabel.current === selectedAreaTypeLabel
      )
        return;
      if (mode === 8) {
        setAutoSelectingMode8(true);
      } else {
        setAutoSelectingMode1(true);
      }
      setState({
        autoSegmenting: true,
      });

      try {
        if (selectedAreaType == 'custom') {
          if (customText !== translate('selection_text1')) {
            const res = await submitTranslate({
              q: customText,
              to: 'en',
              from: 'auto',
            });
            currentTypeRes = res.data?.dst;
          } else {
            currentTypeRes = 'subject';
          }
        } else {
          currentTypeRes = selectedAreaType;
        }
        const url = element.url;
        const imagePath = getImageOssPath(url);
        const cacheKey = getImageSegmentCacheKey(imagePath, 'inpaint');
        const text = mode === 8 ? currentTypeRes : undefined;
        lastSelectedAreaTypeLabel.current = mode === 8 ? selectedAreaTypeLabel : null;
        const { results } = await requestSegmentImage({
          images: [imagePath],
          ...inferenceAPIBucket,
          mode,
          boxThreshold: mode === 8 ? 0.3 : undefined,
          text,
        });

        if (!results || !Array.isArray(results)) {
          throw 'servive error';
        }
        const scale = 1;
        const shapes = results[0]
          .map((item) => {
            return { points: item.contours.map((item) => [item[0][0] * scale, item[0][1] * scale]) };
          })
          .map((item) => {
            return {
              points: item.points.map((item) => [item[0] / element.width, item[1] / element.height]),
              area: calcShapeArea(item),
              id: uniqid(),
            };
          })
          .sort((a, b) => b.area - a.area);

        if (shapes.length) {
          // setSharedData(cacheKey, shapes);
        }

        if (mode === 8 && shapes?.length > 0) {
          setState({
            selections: shapes.map((item) => {
              return {
                ...item,
                sourceType: 'segment',
                type: 'lasso',
              };
            }),
            selected: shapes[0].id,
            mode,
            text,
          });
        } else {
          setState({
            shapes,
          });
        }
        notifySuccess(translate('image_segment_success'));
      } catch (e) {
        setState({ tool: 'brush' });
        lastSelectedAreaTypeLabel.current = null;
        notifyError(translate('image_segment_error'));
      } finally {
        setAutoSelectingMode1(false);
        setAutoSelectingMode8(false);
        setState({
          autoSegmenting: false,
        });
      }
    },
    [selectedAreaType, customText],
  );
  const hanldeChange = useCallback((value, name) => {
    setState({ [name]: value });
  }, []);

  const handleToggleInverted = useCallback(() => {
    setState({ invert: !invert });
  }, [invert]);

  const handleClear = useCallback(() => {
    lastSelectedAreaTypeLabel.current = null;

    setState({
      ...defaultValue,
      shapes,
    });
    setTempState({ shapesCache: [] });
  }, [shapes]);

  useEffect(() => {
    // console.log('xxx shape', shapes)
    // todos fqy 这里想着加一下
    // if(shapes) return
    if (tool === 'mainSelect') {
      handleAutoSelect(8);
    }
    if (tool === 'autoSelectArea') {
      handleAutoSelect(1);
    }
  }, []);
  return (
    <div className="absolute top-full left-1/2 -translate-x-1/2 flex items-center mt-2 z-10">
      {toolbarLeftButton}
      <div className="flex items-center h-[40px] px-3 text-caption-500 text-mini whitespace-nowrap bg-white shadow-light rounded-md">
        {!noAutoSelection && (
          <button
            data-type="autoSelectArea"
            onClick={(e) => {
              handleSwitchType(e);
              if (shapes.length === 0) {
                handleAutoSelect(1);
              }
            }}
            disabled={tool === 'autoSelectArea'}
            className="flex items-center justify-center w-[72px] mr-3 rounded-sm disabled:bg-primary/20 cursor-pointer"
          >
            {autoSelectingMode1 ? (
              <Loader size={12} color="var(--color-caption-500)" />
            ) : (
              <img className="w-[16px] h-[16px] mr-[4px]" src={autoSelection} />
            )}
            <div>{translate('selection_auto')}</div>
          </button>
        )}
        {!noAutoSelection && (
          <button
            className="flex items-center mr-3 cursor-pointer justify-between"
            data-type="mainSelect"
            // disabled={tool === 'mainSelect'}
            onClick={(e) => {
              if (e.currentTarget.contains(e.target)) {
                handleSwitchType(e);
                handleAutoSelect(8);
              }
            }}
          >
            <>
              {autoSelectingMode8 ? (
                <Loader size={12} color="var(--color-caption-500)" />
              ) : (
                <Icon fontSize={16} className={'icon-a-proQuickSelection'} color="inherit" />
              )}
              <Popover width={238} position="top-start" shadow="md" opened={opened}>
                <Popover.Target>
                  <div
                    className="flex items-center justify-between rounded-sm disabled:bg-primary/20 cursor-pointer"
                    onMouseEnter={open}
                    onMouseLeave={close}
                  >
                    <div className="mx-[4px]">
                      <div className="max-w-[72px] truncate">{selectedAreaTypeLabel}</div>
                    </div>
                    <img className="w-[18px] h-[18px]" src={downPull} />
                  </div>
                </Popover.Target>
                <Popover.Dropdown className="box-content" onMouseEnter={open} onMouseLeave={close}>
                  <Radio.Group
                    size="xs"
                    value={selectedAreaType}
                    onChange={(val) => {
                      setSelectedAreaType(val);
                    }}
                    className="main-select-container"
                  >
                    <RadioGroup className="gap-2">
                      {selectedAreaTypeList.map(({ name, label }) => {
                        return (
                          <Radio
                            style={{
                              border: `1px solid ${name === selectedAreaType ? '#339BFF' : '#F4F6F8'}`,
                              borderRadius: '2px',
                            }}
                            size="xs"
                            checked={false}
                            className={`${name === selectedAreaType ? 'bg-[#EBF7FF]' : 'bg-[#F4F6F8]'} px-3 py-1`}
                            key={name}
                            labelElement={'div'}
                            label={<div>{label}</div>}
                            value={name}
                            color="#339BFF"
                          />
                        );
                      })}
                    </RadioGroup>
                  </Radio.Group>
                  {selectedAreaType === 'custom' && (
                    <Input
                      className="w-full mt-2.5"
                      size="xs"
                      value={customText}
                      onChange={(val) => {
                        setCustomText(val.target.value);
                      }}
                    />
                  )}
                  <div className="segment-editor-hr mt-4 mb-3 bg-[#EDEDED]"></div>
                  <div className="flex justify-end">
                    <button
                      className="flex items-center cursor-pointer justify-between px-[12px] py-[4px] rounded-[2px] font-medium text-white bg-[#2D2D2D]"
                      onClick={() => {
                        handleSwitchType('mainSelect');
                        handleAutoSelect(8);
                      }}
                    >
                      {translate('create_selection_area')}
                    </button>
                  </div>
                </Popover.Dropdown>
              </Popover>
            </>
          </button>
        )}
        {!noSegment && (
          <Tooltip withArrow position="bottom" className="scale-90" label={translate('image_segment_tip')}>
            <button
              data-type="segment"
              disabled={tool === 'segment'}
              onClick={handleSwitchType}
              className="flex items-center justify-center w-5 h-5 mr-3 rounded-sm disabled:bg-primary/20"
            >
              {segmenting ? (
                <Loader size={12} color="var(--color-caption-500)" />
              ) : (
                <Icon
                  fontSize={16}
                  className={isChangeFaceMode ? 'shk-face_n' : 'icon-a-proQuickSelection'}
                  color="inherit"
                />
              )}
            </button>
          </Tooltip>
        )}
        <Tooltip withArrow position="bottom" className="scale-90" label={translate('image_brush_tip')}>
          <PopoverSlider
            min={20}
            max={200}
            name="brushSize"
            value={brushSize}
            onChange={hanldeChange}
            label={translate('selection_brush_size_title')}
            tooltip={translate('selection_brush_tooltips')}
          >
            <button
              data-type="brush"
              disabled={tool === 'brush'}
              onClick={handleSwitchType}
              className="flex items-center justify-center w-5 h-5 mr-3 rounded-sm disabled:bg-primary/20"
            >
              <Icon fontSize={16} className="shk-huabi" color="inherit" />
            </button>
          </PopoverSlider>
        </Tooltip>
        <Tooltip withArrow position="bottom" className="scale-90" label={translate('image_eraser_tip')}>
          <PopoverSlider
            min={20}
            max={200}
            name="eraserSize"
            value={eraserSize}
            onChange={hanldeChange}
            label={translate('selection_eraser_size_title')}
            tooltip={translate('selection_eraser_tooltips')}
          >
            <button
              data-type="eraser"
              disabled={tool === 'eraser'}
              onClick={handleSwitchType}
              className="flex items-center justify-center w-5 h-5 mr-3 rounded-sm disabled:bg-primary/20"
            >
              <Icon fontSize={20} className="shk-xiangpi" color="inherit" />
            </button>
          </PopoverSlider>
        </Tooltip>
        <Tooltip withArrow position="bottom" className="scale-90" label={translate('image_lasso_tip')}>
          <button
            data-type="lasso"
            disabled={tool === 'lasso'}
            onClick={handleSwitchType}
            className="flex items-center justify-center w-5 h-5 mr-3 rounded-sm disabled:bg-primary/20"
          >
            <Icon fontSize={16} className="icon-protool" color="inherit" />
          </button>
        </Tooltip>
        <Tooltip withArrow position="bottom" className="scale-90" label={translate('image_rect_tip')}>
          <button
            data-type="rect"
            disabled={tool === 'rect'}
            onClick={handleSwitchType}
            className="flex items-center justify-center w-5 h-5 mr-3 rounded-sm disabled:bg-primary/20"
          >
            <Icon fontSize={16} className="icon-a-prochooseabox" color="inherit" />
          </button>
        </Tooltip>

        <div className="mr-3 h-5 border-l border-[#f4f5f7]" />
        {/* {!noAutoSelection && (
          
        )} */}
        {!noFeathering && (
          <PopoverSlider
            name="extend"
            min={0}
            max={10}
            value={extend}
            onChange={hanldeChange}
            label={translate('selection_feather_title')}
            tooltip={translate('selection_feather_tooltips')}
          >
            <button className="flex items-center justify-center h-5 mr-3 px-1 rounded-sm">
              <Icon fontSize={14} className="icon-mask_padding" color="inherit" />
              <span className="ml-2 text-xs font-medium">{extend}</span>
            </button>
          </PopoverSlider>
        )}
        {!noInvert && (
          <Tooltip
            withArrow
            position="bottom"
            className="scale-90"
            label={translate(invert ? 'selection_invert' : 'selection_noninvert')}
          >
            <button
              onClick={handleToggleInverted}
              className="flex items-center justify-center mr-3 w-5 h-5 disabled:opacity-50 rounded-sm disabled:bg-primary/20"
            >
              <img src={invert ? imgInpaintMaskInvert : imgInpaintMask} className="w-5 h-5" />
            </button>
          </Tooltip>
        )}
        <div className="mr-3 h-5 border-l border-[#f4f5f7]" />
        <Tooltip withArrow position="bottom" className="scale-90" label={translate('common_undo')}>
          <button
            onClick={editorState.back}
            disabled={!canUndo}
            className="flex items-center justify-center w-5 h-5 disabled:opacity-30 rounded-sm"
          >
            <Icon fontSize={24} className="icon-proshangyibu" color="inherit" />
          </button>
        </Tooltip>
        <Tooltip withArrow position="bottom" className="scale-90" label={translate('common_redo')}>
          <button
            onClick={editorState.forward}
            disabled={!canRedo}
            className="flex items-center justify-center w-5 h-5 disabled:opacity-30 ml-3 rounded-sm"
          >
            <Icon fontSize={24} className="icon-proxiayibu" color="inherit" />
          </button>
        </Tooltip>
        {!noClear && (
          <Tooltip withArrow position="bottom" className="scale-90" label={translate('selection_clear')}>
            <button
              onClick={handleClear}
              className="flex items-center justify-center w-5 h-5 ml-3 rounded-sm  disabled:opacity-30"
            >
              <Icon fontSize={20} className="icon-proshauxin" color="inherit" />
            </button>
          </Tooltip>
        )}
      </div>
      {toolbarRightButton}
    </div>
  );
});

/**
 * 在流运行中调用
 * 更新生图参数中
 * @param {} 生图参数
 */
export const updateSegments = async (params) => {
  const element = params.sourceElement;
  const url = element?.url;
  // console.log('xxx updateSegments url', url)
  const mode = params.segments?.mode;
  const text = params.segments?.text;
  if (mode !== 8 || !text || !element) {
    return;
  }
  const imagePath = getImageOssPath(url);
  const { results } = await requestSegmentImage({
    images: [imagePath],
    ...inferenceAPIBucket,
    mode,
    boxThreshold: mode === 8 ? 0.3 : undefined,
    text: mode === 8 ? text : undefined,
  });
  if (!results || !Array.isArray(results)) {
    throw 'servive error';
  }
  const scale = 1;
  const shapes = results[0]
    .map((item) => {
      return { points: item.contours.map((item) => [item[0][0] * scale, item[0][1] * scale]) };
    })
    .map((item) => {
      return {
        points: item.points.map((item) => [item[0] / element?.width, item[1] / element?.height]),
        area: calcShapeArea(item),
        id: uniqid(),
      };
    })
    .sort((a, b) => b.area - a.area)
    .map((item) => {
      return {
        ...item,
        sourceType: 'segment',
        type: 'lasso',
      };
    });
  params.segments.selections = shapes;
};
